/**
 * Service d'autorisation centralisé pour vérifier les permissions
 */
import api from './api';
import { ActionType, ModelType } from '@constants/authorization';

class AuthorizationService {
  /**
   * Vérifie si l'utilisateur peut effectuer une action
   */
  async canPerformAction(action, modelType, modelId = null, context = {}) {
    try {
      const params = {
        action,
        model_type: modelType,
        ...(modelId !== null && { model_id: modelId }),
        ...context
      };

      const response = await api.authorization.canPerform(params);
      return response.data.can_perform;
    } catch (error) {
      console.error('Authorization check failed:', error);
      // En cas d'erreur, on refuse l'accès par sécurité
      return false;
    }
  }

  /**
   * Récupère toutes les permissions pour un modèle
   */
  async getAllPermissions(modelType, modelId = null, context = {}) {
    try {
      const params = {
        model_type: modelType,
        ...(modelId !== null && { model_id: modelId }),
        ...context
      };

      const response = await api.authorization.canPerform(params);
      return response.data.permissions || {};
    } catch (error) {
      console.error('Permissions check failed:', error);
      return {};
    }
  }

  /**
   * Récupère toutes les capacités de l'utilisateur
   */
  async getUserCapabilities() {
    try {
      const response = await api.authorization.capabilities();
      return response.data;
    } catch (error) {
      console.error('Failed to get user capabilities:', error);
      return {
        capabilities: {
          can_create_resources: true,
          can_update_resources: false,
          can_delete_resources: false,
          can_manage_resources: false,
          can_manage_locations: false,
          can_borrow_for_others: false,
          can_admin: false
        }
      };
    }
  }

  /**
   * Récupère les permissions pour une ressource spécifique
   */
  async getResourcePermissions(resourceId) {
    try {
      const response = await api.authorization.resourcePermissions(resourceId);
      return response.data.permissions;
    } catch (error) {
      console.error('Failed to get resource permissions:', error);
      return {
        read: false,
        update: false,
        delete: false,
        borrow: false,
        consume: false,
        refill: false,
        reserve: false
      };
    }
  }

  /**
   * Récupère les permissions pour un emprunt spécifique
   */
  async getBorrowingPermissions(borrowingId) {
    try {
      const response = await api.authorization.borrowingPermissions(borrowingId);
      return response.data.permissions;
    } catch (error) {
      console.error('Failed to get borrowing permissions:', error);
      return {
        read: false,
        return: false,
        update: false,
        delete: false
      };
    }
  }

  // === Méthodes de convenance pour les ressources ===

  async canCreateResource() {
    return this.canPerformAction(ActionType.CREATE, ModelType.RESOURCE);
  }

  async canUpdateResource(resourceId) {
    return this.canPerformAction(ActionType.UPDATE, ModelType.RESOURCE, resourceId);
  }

  async canDeleteResource(resourceId) {
    return this.canPerformAction(ActionType.DELETE, ModelType.RESOURCE, resourceId);
  }

  async canBorrowResource(resourceId, targetUserId = null) {
    const context = targetUserId ? { target_user_id: targetUserId } : {};
    return this.canPerformAction(ActionType.BORROW, ModelType.RESOURCE, resourceId, context);
  }

  async canConsumeResource(resourceId, targetUserId = null) {
    const context = targetUserId ? { target_user_id: targetUserId } : {};
    return this.canPerformAction(ActionType.CONSUME, ModelType.RESOURCE, resourceId, context);
  }

  async canRefillResource(resourceId) {
    return this.canPerformAction(ActionType.REFILL, ModelType.RESOURCE, resourceId);
  }

  async canReserveResource(resourceId) {
    return this.canPerformAction(ActionType.RESERVE, ModelType.RESOURCE, resourceId);
  }

  // === Méthodes de convenance pour les emprunts ===

  async canReturnBorrowing(borrowingId) {
    return this.canPerformAction(ActionType.RETURN, ModelType.BORROWING, borrowingId);
  }

  async canUpdateBorrowing(borrowingId) {
    return this.canPerformAction(ActionType.UPDATE, ModelType.BORROWING, borrowingId);
  }

  async canDeleteBorrowing(borrowingId) {
    return this.canPerformAction(ActionType.DELETE, ModelType.BORROWING, borrowingId);
  }

  // === Méthodes utilitaires ===

  /**
   * Vérifie plusieurs permissions en parallèle
   */
  async checkMultiplePermissions(checks) {
    try {
      const promises = checks.map(check =>
        this.canPerformAction(check.action, check.modelType, check.modelId, check.context)
      );

      const results = await Promise.all(promises);

      return checks.reduce((acc, check, index) => {
        acc[check.key || `${check.action}_${check.modelType}_${check.modelId || 'all'}`] = results[index];
        return acc;
      }, {});
    } catch (error) {
      console.error('Failed to check multiple permissions:', error);
      return {};
    }
  }  /**
   * Cache simple pour éviter les appels répétés
   */
  _cache = new Map();
  _cacheTimeout = 5 * 60 * 1000; // 5 minutes

  async canPerformActionCached(action, modelType, modelId = null, context = {}) {
    const cacheKey = `${action}_${modelType}_${modelId}_${JSON.stringify(context)}`;
    const cached = this._cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this._cacheTimeout) {
      return cached.result;
    }

    const result = await this.canPerformAction(action, modelType, modelId, context);
    this._cache.set(cacheKey, { result, timestamp: Date.now() });

    return result;
  }

  /**
   * Vide le cache (utile après des changements de permissions)
   */
  clearCache() {
    this._cache.clear();
  }
}

export const authorizationService = new AuthorizationService();
export default authorizationService;
